import React from 'react';

// Button component with multiple style variants and sizes
interface BookingButtonProps {
  className?: string;
  children?: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline';  // Visual style variant
  size?: 'sm' | 'md' | 'lg';                      // Button size
}

/**
 * BookingButton: Opens Square booking system in a new tab
 * Reusable button component with configurable styles
 */
const BookingButton: React.FC<BookingButtonProps> = ({ 
  className = '', 
  children = 'Book a Consultation',
  variant = 'primary',
  size = 'md'
}) => {
  // External Square booking URL
  const SQUARE_BOOKING_URL = 'https://book.squareup.com/appointments/cf6vvh6owvq0sd/location/LA818BVGWEZM7/services';

  const handleBookingClick = () => {
    // Opens booking page in new tab with security attributes
    // noopener/noreferrer prevent new page from accessing window.opener
    window.open(SQUARE_BOOKING_URL, '_blank', 'noopener,noreferrer');
  };

  // Base styles applied to all variants
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  // Style variants: different color schemes
  const variantClasses = {
    primary: 'bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500 shadow-sm',
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-900 focus:ring-gray-500',
    outline: 'border border-gray-300 hover:border-gray-400 text-gray-700 hover:text-gray-900 focus:ring-gray-500'
  };

  // Size variants: different padding and text sizes
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  // Combine all classes
  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

  return (
    <button
      onClick={handleBookingClick}
      className={classes}
      title="Book a consultation with our team"
    >
      {children}
    </button>
  );
};

export default BookingButton; 